import React, { useState, useEffect, useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import { AnchoredScale } from './AnchoredScale';
import { RepeatingBlock } from './RepeatingBlock';
import DayFirstDate from "./DayFirstDate";

// ── Constants ──────────────────────────────────────────────────────
const TOOLS_OPTIONS = [
  { value: 'ados2_mod1', label: 'ADOS-2 Module 1' },
  { value: 'ados2_mod2', label: 'ADOS-2 Module 2' },
  { value: 'ados2_mod3', label: 'ADOS-2 Module 3' },
  { value: 'ados2_mod4', label: 'ADOS-2 Module 4' },
  { value: 'dasi2', label: 'DASI-2' },
  { value: 'informal', label: 'Informal observation only' },
  { value: 'other', label: 'Other' },
];
const SETTING_OPTIONS = [
  { value: 'clinic', label: 'Clinic' }, { value: 'school', label: 'School' },
  { value: 'telehealth', label: 'Telehealth' }, { value: 'home', label: 'Home' },
  { value: 'other', label: 'Other' },
];
const PRESENT_OPTIONS = [
  { value: 'client_only', label: 'Client only' }, { value: 'parent', label: 'Parent/carer' },
  { value: 'sibling', label: 'Sibling' }, { value: 'teacher', label: 'Teacher/aide' },
  { value: 'other', label: 'Other' },
];
const ARRIVAL_OPTIONS = [
  { value: 'settled', label: 'Settled' }, { value: 'activated', label: 'Activated/energised' },
  { value: 'anxious', label: 'Anxious or hesitant' }, { value: 'flat', label: 'Flat or withdrawn' },
  { value: 'dysregulated', label: 'Dysregulated' }, { value: 'other', label: 'Other' },
];
const ACTIVITY_OPTIONS = [
  { value: 'free_play', label: 'Free play / construction' },
  { value: 'make_believe', label: 'Make-believe or joint play' },
  { value: 'conversation', label: 'Conversation and reporting' },
  { value: 'demonstration', label: 'Demonstration task' },
  { value: 'picture_scene', label: 'Picture or scene description' },
  { value: 'storytelling', label: 'Storytelling from a book' },
  { value: 'cartoons_humour', label: 'Cartoons or humour' },
  { value: 'emotions', label: 'Emotions discussion' },
  { value: 'social_difficulties', label: 'Social difficulties and annoyance' },
  { value: 'friends_relationships', label: 'Friends, relationships and marriage' },
  { value: 'loneliness', label: 'Loneliness' },
  { value: 'break', label: 'Break or unstructured moment' },
  { value: 'transition', label: 'Transition between tasks' },
  { value: 'waiting_arrival', label: 'Waiting room or arrival' },
  { value: 'with_parent', label: 'With parent/carer present' },
  { value: 'snack', label: 'Snack' },
  { value: 'departure', label: 'Departure' },
  { value: 'other', label: 'Other' },
];
const OBS_TAG_OPTIONS = [
  { value: 'social_overture', label: 'Social overture' },
  { value: 'response_overture', label: 'Response to overture' },
  { value: 'reciprocity', label: 'Back-and-forth reciprocity' },
  { value: 'eye_gaze', label: 'Eye gaze and expression integration' },
  { value: 'gesture', label: 'Gesture use' },
  { value: 'shared_enjoyment', label: 'Shared enjoyment' },
  { value: 'emotional_insight', label: 'Emotional insight' },
  { value: 'relationship_insight', label: 'Insight into relationships' },
  { value: 'language_prosody', label: 'Language style or prosody' },
  { value: 'echoed_scripted', label: 'Echoed or scripted speech' },
  { value: 'repetitive_movement', label: 'Repetitive movement or mannerism' },
  { value: 'focused_interest', label: 'Focused or intense interest' },
  { value: 'sensory', label: 'Sensory response' },
  { value: 'sameness', label: 'Preference for sameness or routine' },
  { value: 'flexibility', label: 'Flexibility and repair' },
  { value: 'masking', label: 'Masking or compensation' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'strength', label: 'Strength or skill' },
];
const DSM5_OPTIONS = [
  { value: 'A1', label: 'A1 Social-emotional reciprocity' },
  { value: 'A2', label: 'A2 Nonverbal communication' },
  { value: 'A3', label: 'A3 Relationships' },
  { value: 'B1', label: 'B1 Stereotyped/repetitive' },
  { value: 'B2', label: 'B2 Sameness/routines' },
  { value: 'B3', label: 'B3 Restricted interests' },
  { value: 'B4', label: 'B4 Sensory' },
  { value: 'strength', label: 'Strength (no criterion)' },
  { value: 'not_diagnostic', label: 'Not diagnostic' },
];
const MASKING_OPTIONS = [
  { value: 'none', label: 'No compensation evident' },
  { value: 'some', label: 'Some learned strategies visible' },
  { value: 'substantial', label: 'Substantial effortful compensation' },
  { value: 'cost', label: 'Compensation with visible cost' },
  { value: 'unable', label: 'Unable to judge from one session' },
];
const CRITERION_STATUS_OPTIONS = [
  { value: 'sufficient', label: 'Sufficient in-session evidence' },
  { value: 'partial', label: 'Partial: weigh with collateral' },
  { value: 'not_observed', label: 'Not observed: rely on history' },
  { value: 'against', label: 'Evidence against' },
];
const QUICK_RATE_ITEMS = [
  { id: 'qr_initiating', label: 'Initiating social overtures' },
  { id: 'qr_responding', label: 'Responding to others\' overtures' },
  { id: 'qr_reciprocity', label: 'Conversational reciprocity (to-and-fro, repair, topic sharing)' },
  { id: 'qr_integration', label: 'Integration of gaze, expression, gesture and speech' },
  { id: 'qr_shared_enjoyment', label: 'Shared enjoyment and social referencing' },
  { id: 'qr_imagination', label: 'Imagination, play and creativity' },
  { id: 'qr_rrb', label: 'Repetitive behaviours, interests or sensory responses' },
  { id: 'qr_flexibility', label: 'Flexibility with transitions and change' },
];
const CRITERIA_LIST = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'B4'];

// Auto-suggestion: tags → DSM-5 criteria
const TAG_TO_CRITERIA = {
  social_overture: ['A1'], response_overture: ['A1'], reciprocity: ['A1'],
  shared_enjoyment: ['A1'], emotional_insight: ['A1'],
  eye_gaze: ['A2'], gesture: ['A2'], language_prosody: ['A2'],
  relationship_insight: ['A3'],
  echoed_scripted: ['B1'], repetitive_movement: ['B1'],
  sameness: ['B2'], focused_interest: ['B3'], sensory: ['B4'],
  strength: ['strength'],
  masking: ['not_diagnostic'], regulation: ['not_diagnostic'], flexibility: ['not_diagnostic'],
};

function suggestCriteria(tags) {
  const set = new Set();
  (tags || []).forEach(tag => {
    (TAG_TO_CRITERIA[tag] || []).forEach(c => set.add(c));
  });
  return [...set];
}

// ── Styles ──
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const sectionSub = { fontSize: '0.8125rem', color: '#6b7280', marginBottom: 16 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };

// ── Component ──
export default function ASDObservationForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  // Parse moment_log from responses (stored as JSON string for backend)
  const [momentLog, setMomentLog] = useState(() => {
    try { return JSON.parse(r.moment_log || '[]'); } catch { return []; }
  });

  // Sync moment_log to responses whenever it changes
  useEffect(() => {
    setResponses(prev => ({ ...prev, moment_log: JSON.stringify(momentLog) }));
  }, [momentLog, setResponses]);

  // Auto-suggest DSM-5 criteria when tags change in a moment entry
  const updateMomentWithAutoSuggest = (index, updated) => {
    if (updated.observation_tags) {
      const suggested = suggestCriteria(updated.observation_tags);
      const existing = updated.dsm5_criteria || [];
      const merged = [...new Set([...suggested, ...existing.filter(c => !suggestCriteria(momentLog[index]?.observation_tags || []).includes(c))])];
      updated.dsm5_criteria = merged;
    }
    const newLog = [...momentLog];
    newLog[index] = updated;
    setMomentLog(newLog);
  };

  // Compute criterion counts from moment log + quick rates
  const criterionCounts = {};
  CRITERIA_LIST.forEach(c => { criterionCounts[c] = { moments: 0, quickRate: false }; });
  momentLog.forEach(entry => {
    (entry.dsm5_criteria || []).forEach(c => {
      if (criterionCounts[c]) criterionCounts[c].moments++;
    });
  });
  QUICK_RATE_ITEMS.forEach(item => {
    const val = r[item.id];
    if (val && val !== 'not_observed') {
      // Map quick-rate items to criteria
      if (item.id.includes('initiating') || item.id.includes('responding') || item.id.includes('reciprocity') || item.id.includes('shared')) {
        if (criterionCounts.A1) criterionCounts.A1.quickRate = true;
      }
      if (item.id.includes('integration')) { if (criterionCounts.A2) criterionCounts.A2.quickRate = true; }
      if (item.id.includes('imagination')) { if (criterionCounts.A3) criterionCounts.A3.quickRate = true; }
      if (item.id.includes('rrb')) { ['B1','B2','B3','B4'].forEach(c => { if (criterionCounts[c]) criterionCounts[c].quickRate = true; }); }
      if (item.id.includes('flexibility')) { if (criterionCounts.B2) criterionCounts.B2.quickRate = true; }
    }
  });

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Section 1 - Session Snapshot */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Session Snapshot</h2>
        <p style={sectionSub}>Quick session details, all on one screen.</p>

        <div style={fieldGap}>
          <label style={labelStyle}>Client initials or code</label>
          <input type="text" style={inputStyle} value={r.client_initials || ''} onChange={e => set('client_initials', e.target.value)} placeholder="e.g. JD" data-testid="client-initials" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Assessment date</label>
          <DayFirstDate label="Date" inputStyle={inputStyle} value={r.assessment_date || ''} onChange={v => set('assessment_date', v)} testId="assessment-date" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Tools administered" options={TOOLS_OPTIONS} value={r.tools_administered || []} onChange={v => set('tools_administered', v)} multi revealField={{ value: 'other', placeholder: 'Specify other tool...' }} revealValue={r.tools_other} onRevealChange={v => set('tools_other', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Setting" options={SETTING_OPTIONS} value={r.setting || ''} onChange={v => set('setting', v)} revealField={{ value: 'other', placeholder: 'Specify setting...' }} revealValue={r.setting_other} onRevealChange={v => set('setting_other', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Present in room" options={PRESENT_OPTIONS} value={r.present_in_room || []} onChange={v => set('present_in_room', v)} multi revealField={{ value: 'other', placeholder: 'Specify who...' }} revealValue={r.present_other} onRevealChange={v => set('present_other', v)} />
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Session length (minutes)</label>
            <input type="number" style={inputStyle} value={r.session_length || ''} onChange={e => set('session_length', e.target.value)} data-testid="session-length" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Breaks taken</label>
            <input type="number" style={inputStyle} value={r.breaks_taken ?? '0'} onChange={e => set('breaks_taken', e.target.value)} data-testid="breaks-taken" />
          </div>
        </div>
        <div style={fieldGap}>
          <ChipSelector label="State on arrival" options={ARRIVAL_OPTIONS} value={r.state_on_arrival || ''} onChange={v => set('state_on_arrival', v)} revealField={{ value: 'other', placeholder: 'Describe state...' }} revealValue={r.arrival_state_other} onRevealChange={v => set('arrival_state_other', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Arrival and separation notes</label>
          <input type="text" style={inputStyle} value={r.arrival_notes || ''} onChange={e => set('arrival_notes', e.target.value)} placeholder="e.g. separated easily after 5 min warm-up alongside parent; brought a comfort item" />
        </div>
      </div>

      {/* Section 2 - Moment Log */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Moment Log</h2>
        <RepeatingBlock
          prompt="Log every notable moment: presses, spontaneous behaviours, quotes, strengths, and anything that surprised you. Aim for 6 to 15 entries. One behaviour per entry."
          entries={momentLog}
          onChange={setMomentLog}
          maxEntries={20}
          addLabel="Add Moment"
          createEmpty={() => ({ activity: '', description: '', quote: '', observation_tags: [], dsm5_criteria: [] })}
          entryTitle={(entry) => {
            const act = ACTIVITY_OPTIONS.find(a => a.value === entry.activity);
            const desc = entry.description ? entry.description.substring(0, 40) + (entry.description.length > 40 ? '...' : '') : '';
            return act ? `${act.label}${desc ? ': ' + desc : ''}` : (desc || 'New moment');
          }}
          renderEntry={(entry, index, updateEntry) => (
            <div>
              <div style={fieldGap}>
                <label style={labelStyle}>Activity or press</label>
                <select
                  style={inputStyle}
                  value={entry.activity || ''}
                  onChange={e => updateEntry({ ...entry, activity: e.target.value })}
                >
                  <option value="">Select activity...</option>
                  {ACTIVITY_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                {entry.activity === 'other' && (
                  <input type="text" style={{ ...inputStyle, marginTop: 6 }} placeholder="Describe activity..." value={entry.activity_other || ''} onChange={e => updateEntry({ ...entry, activity_other: e.target.value })} />
                )}
              </div>
              <div style={fieldGap}>
                <label style={labelStyle}>What happened <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea style={textareaStyle} value={entry.description || ''} onChange={e => updateEntry({ ...entry, description: e.target.value })} placeholder="Concrete and observable. What did they do, in what context, and what happened around it?" />
              </div>
              <div style={fieldGap}>
                <label style={labelStyle}>Verbatim quote</label>
                <input type="text" style={inputStyle} value={entry.quote || ''} onChange={e => updateEntry({ ...entry, quote: e.target.value })} placeholder="Their exact words, if any" />
              </div>
              <div style={fieldGap}>
                <ChipSelector label="Observation tags" options={OBS_TAG_OPTIONS} value={entry.observation_tags || []} onChange={tags => updateMomentWithAutoSuggest(index, { ...entry, observation_tags: tags })} multi />
              </div>
              <div style={fieldGap}>
                <ChipSelector label="DSM-5 criterion mapping (auto-suggested, editable)" options={DSM5_OPTIONS} value={entry.dsm5_criteria || []} onChange={v => updateEntry({ ...entry, dsm5_criteria: v })} multi />
              </div>
            </div>
          )}
        />
      </div>

      {/* Section 3 - Domain Quick-Rate */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Domain Quick-Rate</h2>
        <p style={sectionSub}>Rate each domain on a 4-point anchored scale. Add a concrete example if not already in the Moment Log.</p>
        {QUICK_RATE_ITEMS.map(item => (
          <div key={item.id} style={fieldGap}>
            <AnchoredScale
              label={item.label}
              value={r[item.id] ?? null}
              onChange={v => set(item.id, v)}
              evidenceValue={r[`${item.id}_evidence`] || ''}
              onEvidenceChange={v => set(`${item.id}_evidence`, v)}
            />
          </div>
        ))}
      </div>

      {/* Section 4 - Masking, Compensation and Effort */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Masking, Compensation and Effort</h2>
        <div style={fieldGap}>
          <ChipSelector label="Overall impression" options={MASKING_OPTIONS} value={r.masking_impression || ''} onChange={v => set('masking_impression', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Evidence</label>
          <textarea style={textareaStyle} value={r.masking_evidence || ''} onChange={e => set('masking_evidence', e.target.value)} placeholder="Discrepancy between polish and effort, rehearsed-sounding responses, self-monitoring comments, end-of-session change, recovery cost described by client or parent" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Client&apos;s own words about effort</label>
          <input type="text" style={inputStyle} value={r.masking_client_words || ''} onChange={e => set('masking_client_words', e.target.value)} placeholder="Their words about how hard it was" />
        </div>
      </div>

      {/* Section 5 - Cross-Context and Incongruencies */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Cross-Context and Incongruencies</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Differences between in-session presentation and collateral report</label>
          <textarea style={textareaStyle} value={r.cross_context || ''} onChange={e => set('cross_context', e.target.value)} placeholder="Where does today's presentation sit against parent, school and self-report? Same, milder, or more evident, and in what way?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Off-protocol moments</label>
          <textarea style={textareaStyle} value={r.off_protocol || ''} onChange={e => set('off_protocol', e.target.value)} placeholder="Waiting room, transitions, with parent, packing up. Often the richest data." />
        </div>
      </div>

      {/* Section 6 - Criterion Sufficiency Check */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Criterion Sufficiency Check</h2>
        <p style={sectionSub}>Auto-generated from your Moment Log entries and Quick-Rate data.</p>
        {CRITERIA_LIST.map(c => {
          const data = criterionCounts[c];
          const criterionLabel = DSM5_OPTIONS.find(o => o.value === c)?.label || c;
          return (
            <div key={c} style={{ marginBottom: 16, padding: 12, backgroundColor: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e3a5f' }}>{criterionLabel}</span>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {data.moments} moment{data.moments !== 1 ? 's' : ''} logged
                  {data.quickRate && ' + quick-rate evidence'}
                </span>
              </div>
              <ChipSelector
                options={CRITERION_STATUS_OPTIONS}
                value={r[`criterion_${c}_status`] || ''}
                onChange={v => set(`criterion_${c}_status`, v)}
              />
              <input type="text" style={{ ...inputStyle, marginTop: 6, fontSize: '0.8125rem' }} value={r[`criterion_${c}_notes`] || ''} onChange={e => set(`criterion_${c}_notes`, e.target.value)} placeholder="Optional note..." />
            </div>
          );
        })}
      </div>

      {/* General Clinical Observations */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>General Clinical Observations</h2>
        <div style={fieldGap}><label style={labelStyle}>General clinical observations</label><textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.general_clinical_observations || ''} onChange={e => set('general_clinical_observations', e.target.value)} /></div>
        <div style={fieldGap}><label style={labelStyle}>General client quotes</label><textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.general_client_quotes || ''} onChange={e => set('general_client_quotes', e.target.value)} /></div>
      </div>

      {/* Section 7 - Synthesis */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Synthesis</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Key observational themes <span style={{ color: '#ef4444' }}>*</span></label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.synthesis_themes || ''} onChange={e => set('synthesis_themes', e.target.value)} placeholder="3 to 5 sentences. What characterised this session?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Strengths and resources observed <span style={{ color: '#ef4444' }}>*</span></label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.synthesis_strengths || ''} onChange={e => set('synthesis_strengths', e.target.value)} placeholder="Skills, interests, connection points, regulation strategies, conditions under which they thrived" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Formulation notes and next steps</label>
          <textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.synthesis_formulation || ''} onChange={e => set('synthesis_formulation', e.target.value)} placeholder="Working hypotheses, environment-fit observations, what to check in collateral, remaining questions" />
        </div>
      </div>
    </div>
  );
}
